import { Config, Stage } from '@3fourteen/core';

import { base64toBlob } from 'services/base64toBlob';
import { IS_PRODUCTION } from 'services/environment';

export type DynamicChartResponse =
  | { type: 'html'; content: string }
  | { type: 'pdf'; url: string }
  | { type: 'json'; content: string };

export async function fetchDynamicChart(url: string): Promise<DynamicChartResponse> {
  const stage = IS_PRODUCTION ? Stage.prod : Stage.dev;

  if (!url) {
    throw new Error('No url provided');
  }

  const res = await fetch(`${Config[stage].apiUrl}${url}`, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf, text/html, application/json',
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';

    // Only try to parse as JSON if the response is actually JSON
    if (contentType.includes('application/json')) {
      const body = await res.json();
      throw new Error(body.error?.message || 'Failed to fetch chart');
    } else {
      // For non-JSON error responses, use status text
      throw new Error(`Failed to fetch chart: ${res.status} ${res.statusText}`);
    }
  }

  const contentType = res.headers.get('content-type') || '';

  // Check if response is JSON
  if (contentType.includes('application/json')) {
    const jsonText = await res.text();
    return { type: 'json', content: jsonText };
  }

  // Check if response is HTML
  if (contentType.includes('text/html')) {
    const html = await res.text();
    return { type: 'html', content: html };
  }

  // Otherwise, treat as PDF: get arrayBuffer and create blob directly
  const chart = await res.arrayBuffer();
  const blob = new Blob([chart], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  return { type: 'pdf', url: blobUrl };
}
